import { createAsyncThunk, createSlice } from "@reduxjs/toolkit"
import { apim } from "@/constants/api"
import { showToast } from "@/store/features/genericSlice"
import { editAddressEndPoint, GET_PROFILE_ENDPOINT } from "@/constants"

const initialState = {
  status: "",
  error: "",
  addresses: [],
  profileVersion: null,
  defaultId: "",
}

export const editAddress = createAsyncThunk(
  "customer/address/edit",
  async (payload, thunkAPI) => {
    const {
      label = "",
      county = "",
      addressLine2 = "",
      addressLine3 = "",
      country = "GB",
    } = payload.address ?? {}
    const actions = [
      {
        action: "changeAddress",
        addressId: payload.id,
        address: {
          ...payload.address,
          country,
          custom: {
            fields: {
              label,
              county,
              addressLine2,
              addressLine3,
            },
            type: {
              typeId: "type",
              key: "custom-address",
            },
          },
        },
      },
    ]
    if (payload.address.default) {
      actions.push({
        action: "setDefaultShippingAddress",
        addressId: payload.id,
      })
    }
    const body = {
      version: payload.profileVersion,
      actions,
    }
    try {
      const { data, status } = await apim.post(`${editAddressEndPoint}`, body)
      if (status !== 200) {
        thunkAPI.dispatch(
          showToast({
            message: "Failed to edit address",
            isVisible: true,
          })
        )
        return { error: "Failed to edit address" }
      }
      return data
    } catch (error) {
      console.error("Failed to edit address", error)
    }
  }
)

export const addAddress = createAsyncThunk(
  "customer/address/add",
  async (payload, thunkAPI) => {
    const {
      label = "",
      county = "",
      addressLine2 = "",
      addressLine3 = "",
      country = "GB",
    } = payload.address ?? {}
    const body = {
      version: payload.profileVersion,
      actions: [
        {
          action: "addAddress",
          address: {
            ...payload.address,
            country,
            custom: {
              fields: {
                label,
                county,
                addressLine2,
                addressLine3,
              },
              type: {
                typeId: "type",
                key: "custom-address",
              },
            },
          },
        },
      ],
    }
    try {
      const { data, status } = await apim.post(`${editAddressEndPoint}`, body)
      if (status !== 200) {
        thunkAPI.dispatch(
          showToast({
            message: "Failed to add address",
            isVisible: true,
          })
        )
        return { error: "Failed to add address" }
      }
      return data
    } catch (error) {
      console.error("Failed to add address", error)
    }
  }
)

export const setDefaultAddress = createAsyncThunk(
  "customer/default/address",
  async (payload, thunkAPI) => {
    const body = {
      version: payload.profileVersion,
      actions: [
        {
          action: "setDefaultShippingAddress",
          addressId: payload.id,
        },
      ],
    }
    try {
      const { data, status } = await apim.post(`${editAddressEndPoint}`, body)
      if (status !== 200) {
        thunkAPI.dispatch(
          showToast({
            message: "Failed to set default address",
            isVisible: true,
          })
        )
        return { error: "Failed to set default address" }
      }
      return data
    } catch (error) {
      console.error("Failed to set default address", error)
    }
  }
)

export const getProfile = createAsyncThunk("customer/me", async thunkAPI => {
  try {
    const { data, status } = await apim.get(`${GET_PROFILE_ENDPOINT}`)
    if (status !== 200) {
      thunkAPI.dispatch(
        showToast({
          message: "Failed to get profile",
          isVisible: true,
        })
      )
      return { error: "Failed to get profile" }
    }
    return data
  } catch (error) {
    console.error("Failed to get profile", error)
  }
})

export const createDividoApplication = createAsyncThunk(
  "createDividoApplication",
  async (payload, thunkAPI) => {
    const {
      planId = "",
      deposit = 0,
      errMssg = "",
      checkoutPagePath = "/",
    } = payload

    try {
      const state = thunkAPI.getState()
      const {
        cart: {
          id: cartId = "",
          version = 0,
          lineItems = [],
          totalTax: { centAmount: taxAmount = 0 } = {},
          shippingAddress: {
            firstName = "",
            lastName = "",
            email = "",
            mobile = "",
          } = {},
        },
      } = state.cart

      const orderItems = lineItems.map(item => {
        const {
          name = {},
          quantity = 1,
          price: {
            discounted: {
              value: { centAmount: productDiscountPrice = 0 } = {},
            } = {},
            value: { centAmount: salePrice = 0 },
          } = {},
          discountedPrice: {
            value: { centAmount: cartDiscountPrice = 0 } = {},
          } = {},
        } = item
        return {
          name: name[payload.locale] ?? "",
          quantity: quantity,
          price:
            cartDiscountPrice > 0
              ? cartDiscountPrice
              : productDiscountPrice > 0
              ? productDiscountPrice
              : salePrice,
        }
      })

      orderItems.push({
        name: "VAT",
        quantity: 1,
        price: taxAmount,
      })

      const body = {
        finance_plan_id: planId,
        deposit_percentage: deposit,
        applicants: [
          {
            email,
            firstName,
            lastName,
            phoneNumber: mobile.replaceAll(" ", ""),
          },
        ],
        order_items: orderItems,
        urls: {
          merchant_redirect_url: `${checkoutPagePath}?paydiv=success`,
          merchant_checkout_url: `${checkoutPagePath}?paydiv=failure`,
        },
        metadata: {
          cartId,
          version,
          paymentSource: "Divido",
          planOption: "12 months",
        },
      }

      const { data, status } = await apim.post(
        "/payment/divido/application",
        body
      )
      if (status !== 201) {
        thunkAPI.dispatch(
          showToast({
            isVisible: true,
            message: errMssg,
          })
        )
        return { error: errMssg }
      }
      const {
        data: {
          urls: { application_url = "/" },
        },
      } = data
      window.location = application_url
    } catch (error) {
      thunkAPI.dispatch(
        showToast({
          isVisible: true,
          message: errMssg,
        })
      )
      console.error(errMssg, error)
    }
  }
)

export const paymentricPayment = createAsyncThunk(
  "paymentricPayment",
  async (payload, thunkAPI) => {
    try {
      const res = await apim.post("/checkout/payment", payload)
      return res.data
    } catch {
      return {}
    }
  }
)

export const checkoutSlice = createSlice({
  name: "checkout",
  initialState,
  reducers: {},
  extraReducers(builder) {
    builder
      .addCase(editAddress.pending, state => {
        state.status = "loading"
      })
      .addCase(editAddress.fulfilled, (state, action) => {
        state.status = "succeeded"
        const {
          addresses,
          version,
          defaultShippingAddressId = "",
        } = action.payload
        state.addresses = addresses
        state.profileVersion = version
        state.defaultId = defaultShippingAddressId
      })
      .addCase(editAddress.rejected, (state, action) => {
        state.status = "failed"
        state.error = action.error.message
      })
      .addCase(setDefaultAddress.pending, state => {
        state.status = "loading"
      })
      .addCase(setDefaultAddress.fulfilled, (state, action) => {
        state.status = "succeeded"
        const { addresses, version, defaultShippingAddressId } = action.payload
        state.addresses = addresses
        state.profileVersion = version
        state.defaultId = defaultShippingAddressId
      })
      .addCase(setDefaultAddress.rejected, (state, action) => {
        state.status = "failed"
        state.error = action.error.message
      })
      .addCase(getProfile.pending, state => {
        state.status = "loading"
      })
      .addCase(getProfile.fulfilled, (state, action) => {
        state.status = "succeeded"
        const { addresses, version, defaultShippingAddressId } = action.payload
        state.addresses = addresses
        state.profileVersion = version
        state.defaultId = defaultShippingAddressId
      })
      .addCase(getProfile.rejected, (state, action) => {
        state.status = "failed"
        state.error = action.error.message
      })
      .addCase(addAddress.pending, state => {
        state.status = "loading"
      })
      .addCase(addAddress.fulfilled, (state, action) => {
        state.status = "succeeded"
        const { addresses, version, defaultShippingAddressId } = action.payload
        state.addresses = addresses
        state.profileVersion = version
        state.defaultId = defaultShippingAddressId
      })
      .addCase(addAddress.rejected, (state, action) => {
        state.status = "failed"
        state.error = action.error.message
      })
      .addCase(createDividoApplication.pending, state => {
        state.status = "loading"
      })
      .addCase(createDividoApplication.fulfilled, state => {
        state.status = "succeeded"
      })
      .addCase(createDividoApplication.rejected, (state, action) => {
        state.status = "failed"
        state.error = action.error.message
      })
      .addCase(paymentricPayment.pending, state => {
        state.status = "loading"
      })
      .addCase(paymentricPayment.rejected, (state, action) => {
        state.status = "failed"
        state.error = action.error.message
      })
      .addCase(paymentricPayment.fulfilled, (state, action) => {
        state.status = "succeeded"
      })
  },
})

export const checkoutState = state => state.checkout
